import type { ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';
import { IconButton } from '@shared/ui/icon-button';
import { Spinner } from '@shared/ui/spinner';

import {
  DIALOG_BODY_AFTER_SUBHEADER_CLASS,
  DIALOG_BODY_CLASS,
  DIALOG_FOOTER_CLASS,
  DIALOG_HEADER_ACTIONS_CLASS,
  DIALOG_HEADER_CLASS,
  DIALOG_HEADERLESS_BODY_CLASS,
  DIALOG_HEADERLESS_TITLE_CLASS,
  DIALOG_LOADING_DIM_CLASS,
  DIALOG_SUBHEADER_CLASS,
  type DialogStatus,
} from './dialog.constant';
import { DialogBodySkeleton, DialogTitleSkeleton } from './dialog.skeleton.ui';
import { DIALOG_TEXT } from './dialog.text';

export interface DialogPartsProps {
  titleId: string;
  bodyId: string;
  title: ReactNode;
  /** Figma `Body` + `Show body text` */
  body?: ReactNode;
  /** Figma `Content` + `Show content` */
  content?: ReactNode;
  status: DialogStatus;
  /** 오른쪽 위 닫기(×). 없으면 그리지 않는다(AlertDialog) */
  onClose?: () => void;
  closeLabel?: string;
  /** 머리 오른쪽(닫기 왼쪽)의 버튼 · 범례 등. 머리와 함께 고정된다 */
  headerActions?: ReactNode;
  /** 머리 아래 고정 줄(필터 · 탭 등). 본문이 스크롤돼도 남는다 */
  subheader?: ReactNode;
  /** 바닥의 버튼들. 없으면 바닥을 그리지 않는다(Figma `Show footer`) */
  footer?: ReactNode;
  /** 본문에 붙는다(간격 · 최대 높이 등) */
  bodyClassName?: string;
  /** init 본문 막대 너비(px) */
  skeletonLines?: readonly (number | string)[];
  /**
   * 머리 줄 없이 제목을 본문 첫 줄(보통 굵기)로 그린다 — AlertDialog 확인 창.
   * 닫기 · 머리 오른쪽 · 머리 아래 줄은 쓰지 않는다
   */
  headerless?: boolean;
}

/**
 * Dialog · AlertDialog 의 안쪽: 머리(제목 + 머리 오른쪽 + 닫기) · 머리 아래 줄 · 본문(글 + 내용 자리) · 바닥(버튼 가운데 정렬).
 * 머리 · 머리 아래 줄 · 바닥은 고정이고 **본문만 스크롤된다**(판 높이는 ModalLayer 가 화면 안으로 제한한다).
 * init = 제목 · 본문 막대, loading = 머리 · 본문 · 바닥을 흐리게 + 가운데 Spinner.
 */
export function DialogParts({
  titleId,
  bodyId,
  title,
  body,
  content,
  status,
  onClose,
  closeLabel = DIALOG_TEXT.close,
  headerActions,
  subheader,
  footer,
  bodyClassName,
  skeletonLines,
  headerless = false,
}: DialogPartsProps) {
  const init = status === 'init';
  const loading = status === 'loading';

  if (headerless) {
    return (
      <>
        <div
          data-slot="body"
          className={cn(DIALOG_BODY_CLASS, DIALOG_HEADERLESS_BODY_CLASS, bodyClassName)}
        >
          {init ? <DialogBodySkeleton lines={skeletonLines} /> : null}
          <h2
            id={titleId}
            className={cn(
              DIALOG_HEADERLESS_TITLE_CLASS,
              init && 'sr-only',
              loading && DIALOG_LOADING_DIM_CLASS,
            )}
          >
            {title}
          </h2>
          {!init && body !== undefined ? (
            <p
              id={bodyId}
              className={cn('text-fg-muted typo-body', loading && DIALOG_LOADING_DIM_CLASS)}
            >
              {body}
            </p>
          ) : null}
          {!init && content !== undefined ? (
            <div data-slot="content" className={cn(loading && DIALOG_LOADING_DIM_CLASS)}>
              {content}
            </div>
          ) : null}
        </div>
        {footer ? (
          <div
            data-slot="footer"
            className={cn(DIALOG_FOOTER_CLASS, loading && DIALOG_LOADING_DIM_CLASS)}
          >
            {footer}
          </div>
        ) : null}
        {loading ? (
          <Spinner size="md" data-slot="spinner" className="absolute inset-0 m-auto" />
        ) : null}
      </>
    );
  }

  return (
    <>
      <div
        data-slot="header"
        className={cn(DIALOG_HEADER_CLASS, loading && DIALOG_LOADING_DIM_CLASS)}
      >
        {init ? <DialogTitleSkeleton /> : null}
        <h2
          id={titleId}
          className={cn('min-w-0 flex-1 text-fg-default typo-title', init && 'sr-only')}
        >
          {title}
        </h2>
        {headerActions !== undefined && !init ? (
          <div data-slot="header-actions" className={DIALOG_HEADER_ACTIONS_CLASS}>
            {headerActions}
          </div>
        ) : null}
        {onClose ? (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={closeLabel}
            icon={<Icon name="close" size="lg" />}
            onClick={onClose}
          />
        ) : null}
      </div>
      {subheader !== undefined && !init ? (
        <div
          data-slot="subheader"
          className={cn(DIALOG_SUBHEADER_CLASS, loading && DIALOG_LOADING_DIM_CLASS)}
        >
          {subheader}
        </div>
      ) : null}
      {init || body !== undefined || content !== undefined ? (
        <div
          data-slot="body"
          className={cn(
            DIALOG_BODY_CLASS,
            subheader !== undefined && !init && DIALOG_BODY_AFTER_SUBHEADER_CLASS,
            bodyClassName,
          )}
        >
          {init ? (
            <DialogBodySkeleton lines={skeletonLines} />
          ) : (
            <>
              {body !== undefined ? (
                <p
                  id={bodyId}
                  className={cn('text-fg-muted typo-body', loading && DIALOG_LOADING_DIM_CLASS)}
                >
                  {body}
                </p>
              ) : null}
              {content !== undefined ? (
                <div data-slot="content" className={cn(loading && DIALOG_LOADING_DIM_CLASS)}>
                  {content}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      {footer ? (
        <div
          data-slot="footer"
          className={cn(DIALOG_FOOTER_CLASS, loading && DIALOG_LOADING_DIM_CLASS)}
        >
          {footer}
        </div>
      ) : null}
      {loading ? (
        <Spinner size="md" data-slot="spinner" className="absolute inset-0 m-auto" />
      ) : null}
    </>
  );
}
